import React, { useState, useRef, useEffect } from 'react';
import { useCaptions } from '../context/CaptionContext';
import { useI18n, rich } from '../../i18n';

// Icons
const FontIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 7V4h16v3" />
    <path d="M9 20h6" />
    <path d="M12 4v16" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Font options data - separated by script
const ARABIC_FONTS = [
  { value: 'Noto Naskh Arabic', label: 'Noto Naskh', desc: 'Default', preview: 'كيفاش' },
  { value: 'Amiri', label: 'Amiri', desc: 'Classic', preview: 'كيفاش' },
  { value: 'IBM Plex Sans Arabic', label: 'IBM Plex Sans', desc: 'Modern', preview: 'كيفاش' },
  { value: 'Tajawal', label: 'Tajawal', desc: 'Geometric', preview: 'كيفاش' },
  { value: 'Cairo', label: 'Cairo', desc: 'Bold', preview: 'كيفاش' },
];

const LATIN_FONTS = [
  { value: 'Inter', label: 'Inter', desc: 'Clean', preview: 'Hello' },
  { value: 'Plus Jakarta Sans', label: 'Plus Jakarta', desc: 'Modern', preview: 'Hello' },
  { value: 'Roboto', label: 'Roboto', desc: 'Classic', preview: 'Hello' },
  { value: 'Georgia', label: 'Georgia', desc: 'Serif', preview: 'Hello' },
];

// Custom Font Selector Component (single script)
const FontSelector = ({ value, onChange, fonts, label }) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Find current font
  const currentFont = fonts.find(f => f.value === value) || fonts[0];
  
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition-all text-start"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div 
            className="text-lg leading-none" 
            style={{ fontFamily: currentFont.value }}
            dir={currentFont.preview === 'Hello' ? 'ltr' : 'rtl'}
          >
            {currentFont.preview}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-700 truncate">{currentFont.label}</div>
            <div className="text-xs text-slate-400">{t(currentFont.desc)}</div>
          </div>
        </div>
        <div className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDownIcon />
        </div>
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 py-1 bg-white rounded-xl border border-slate-200 shadow-xl max-h-64 overflow-y-auto">
          {fonts.map((font) => (
            <button
              key={font.value}
              onClick={() => {
                onChange(font.value);
                setIsOpen(false);
              }}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50 transition-colors ${
                value === font.value ? 'bg-brand/5' : ''
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div 
                  className="w-16 text-base leading-none text-slate-600" 
                  style={{ fontFamily: font.value }}
                  dir={font.preview === 'Hello' ? 'ltr' : 'rtl'}
                >
                  {font.preview}
                </div>
                <div className="min-w-0 text-start">
                  <div className="text-sm font-medium text-slate-700">{font.label}</div>
                  <div className="text-xs text-slate-400">{t(font.desc)}</div>
                </div>
              </div>
              {value === font.value && (
                <div className="text-brand">
                  <CheckIcon />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({ projectLanguage = 'ar' }) => {
  const {
    themeSettings,
    setThemeSettings,
    displayMode,
    phraseDisplayMode,
    applyPhraseDisplayMode,
    reorganizeSkeletons,
    captionData,
    autoScrollEnabled,
    setAutoScrollEnabled,
  } = useCaptions();
  const { t } = useI18n();

  // Check if there are any phrases with alternatives
  const hasPhrases = captionData?.phrases?.length > 0;
  
  // Check if this is a non-Arabic language (English/French/Arabizi)
  const isNonArabic = ['en', 'fr', 'english', 'french', 'arabizi'].includes(projectLanguage);
  const isArabiziMode = displayMode === 'arabizi';

  // Settings that trigger auto-reorganization
  const chunkSettings = ['wordsPerChunk', 'charsPerChunk', 'chunkMode', 'separateScriptChunks', 'silenceAwareChunks', 'punctuationAwareChunks', 'splitBySpeaker'];

  const handleChange = (key, value) => {
    const newSettings = {
      ...themeSettings,
      [key]: value
    };
    setThemeSettings(newSettings);

    // Auto-reorganize when chunk-related settings change
    if (chunkSettings.includes(key)) {
      reorganizeSkeletons({
        mode: newSettings.chunkMode || 'words',
        maxWords: newSettings.wordsPerChunk,
        maxChars: newSettings.charsPerChunk,
        separateScripts: newSettings.separateScriptChunks,
        silenceAware: newSettings.silenceAwareChunks,
        punctuationAware: newSettings.punctuationAwareChunks,
        splitBySpeaker: newSettings.splitBySpeaker
      });
    }
  };

  // Check if any words have speaker info
  const hasSpeakerInfo = captionData?.words?.some(w => w.speaker);

  return (
    <div className="p-6 space-y-10 overflow-y-auto flex-1">
      {/* Behavior Section */}
      <div className="space-y-4" data-tour="settings-behavior">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('Behavior')}</h4>
        <div className="flex items-center justify-between p-4 rounded-2xl border shadow-sm bg-white border-slate-200">
          <div className="flex-1 min-w-0 me-4">
            <div className="text-sm font-bold text-slate-700">{t('Auto Follow')}</div>
            <div className="text-xs mt-0.5 text-slate-500">{t('Keep the current word in view while playing.')}</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={autoScrollEnabled}
              onChange={(e) => setAutoScrollEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand bg-slate-200"></div>
          </label>
        </div>
      </div>

      {/* Typography Section */}
      <div className="space-y-4" data-tour="settings-typography">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('Typography')}</h4>
          <span className="text-[11px] text-slate-400">{t('Editor & preview')}</span>
        </div>

        {/* Font Size Slider */}
        <div className="space-y-3 p-4 rounded-2xl border shadow-sm bg-white border-slate-200">
          <div className="flex justify-between text-sm font-bold text-slate-700">
            <div className="flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 7V4h16v3" />
                <path d="M9 20h6" />
                <path d="M12 4v16" />
              </svg>
              <span>{t('Font Size')}</span>
            </div>
          </div>
          <div className="text-xs text-slate-500">{t('Balance readability and screen space.')}</div>
          <div className="flex items-center gap-3 text-[10px] text-slate-400">
            <span>{t('Small')}</span>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.125"
              value={themeSettings.proseFontSize || 1.25}
              onChange={(e) => handleChange('proseFontSize', parseFloat(e.target.value))}
              className="flex-1 h-2 rounded-full appearance-none cursor-pointer accent-brand bg-slate-200"
            />
            <span>{t('Large')}</span>
          </div>
        </div>

        {/* Font Selector */}
        <div className="space-y-3 p-4 rounded-2xl border shadow-sm bg-white border-slate-200">
          <div className="flex justify-between text-sm font-bold text-slate-700">
            <div className="flex items-center gap-2">
              <FontIcon />
              <span>{t('Font')}</span>
            </div>
          </div>
          <div className="text-xs text-slate-500">
            {t(isNonArabic ? 'Choose a font for your captions.' : 'Choose fonts for Arabic and Latin text.')}
          </div>
          
          {/* Arabic Font - only show for Arabic */}
          {!isNonArabic && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                <span className="text-base leading-none">ع</span> {t('Arabic')}
              </label>
              <FontSelector
                value={themeSettings.proseFontFamily || 'Noto Naskh Arabic'}
                onChange={(val) => handleChange('proseFontFamily', val)}
                fonts={ARABIC_FONTS}
              />
            </div>
          )}
          
          {/* Latin Font */}
          <div className="space-y-1.5">
            {!isNonArabic && (
              <label className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                <span className="text-base font-bold leading-none">A</span> {t('Latin')}
              </label>
            )}
            <FontSelector
              value={themeSettings.latinFontFamily || 'Inter'}
              onChange={(val) => handleChange('latinFontFamily', val)}
              fonts={LATIN_FONTS}
            />
          </div>
        </div>
      </div>

      {/* Punctuation Section */}
      <div className="space-y-4" data-tour="settings-punctuation">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('Punctuation')}</h4>
        
        <div className="flex items-center justify-between p-4 rounded-2xl border shadow-sm bg-white border-slate-200">
          <div className="flex-1 min-w-0 me-4">
            <div className="text-sm font-bold text-slate-700">{t('Show Punctuation')}</div>
            <div className="text-xs mt-0.5 text-slate-500">{t('Include ، ؟ ! . in captions')}</div>
                  </div>
          <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={themeSettings.showPunctuation !== false}
                    onChange={(e) => handleChange('showPunctuation', e.target.checked)}
              className="sr-only peer"
                  />
            <div className="w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand bg-slate-200"></div>
                </label>
              </div>
            </div>

            {/* Foreign Phrases Display Mode - only show for Arabic */}
            {hasPhrases && !isNonArabic && !isArabiziMode && (
        <div className="space-y-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('Foreign Phrases')}</h4>
          <div className="text-xs text-slate-500">{t('Pick a consistent style for non‑Arabic phrases.')}</div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'default', label: 'Default' },
              { id: 'code_switched', label: 'Latin' },
              { id: 'translated', label: 'Translated' }
            ].map((mode) => (
                    <button
                key={mode.id}
                onClick={() => applyPhraseDisplayMode(mode.id)}
                className={`flex-1 min-w-[110px] px-3 py-2 rounded-xl text-sm font-bold text-center transition-all ${
                  phraseDisplayMode === mode.id
                    ? 'bg-brand text-white shadow-lg shadow-brand/20'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {t(mode.label)}
                    </button>
            ))}
                  </div>
          <div className="text-xs mt-2 text-slate-400">
                    {phraseDisplayMode === 'default' && t('Foreign phrases shown in Arabic script')}
                    {phraseDisplayMode === 'code_switched' && t('Foreign phrases shown in original Latin script')}
                    {phraseDisplayMode === 'translated' && t('All foreign phrases translated')}
                </div>
              </div>
            )}

            {/* Arabizi summary: phrase mode is fixed and hidden */}
            {hasPhrases && isArabiziMode && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('Foreign Phrases')}</h4>
                <div className="p-3 rounded-xl bg-slate-100 text-xs text-slate-600">
                  {t('Locked to Latin (code-switched) in Arabizi mode.')}
                </div>
              </div>
            )}

      {/* Chunk Settings Section */}
      <div className="space-y-4" data-tour="settings-chunks">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('Chunk Settings')}</h4>
        
        {/* Chunk Mode Selector */}
        <div className="grid grid-cols-2 gap-2 p-1 rounded-xl border bg-white border-slate-200">
          <button
            onClick={() => handleChange('chunkMode', 'words')}
            className={`px-3 py-2 rounded-lg text-sm font-bold transition-all ${
              (themeSettings.chunkMode || 'words') === 'words'
                ? 'bg-brand text-white shadow-md'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('By Words')}
          </button>
          <button
            onClick={() => handleChange('chunkMode', 'characters')}
            className={`px-3 py-2 rounded-lg text-sm font-bold transition-all ${
              themeSettings.chunkMode === 'characters'
                ? 'bg-brand text-white shadow-md'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('By Characters')}
          </button>
        </div>

        {/* Words per Chunk - shown when mode is 'words' */}
        {(themeSettings.chunkMode || 'words') === 'words' && (
          <div className="space-y-3 p-4 rounded-2xl border shadow-sm bg-white border-slate-200">
            <div className="flex justify-between text-sm font-bold text-slate-700">
              <span>{t('Words per chunk')}</span>
              <span className="text-brand">{themeSettings.wordsPerChunk}</span>
            </div>
            <input
              type="range"
              min="1"
              max="12"
              value={themeSettings.wordsPerChunk}
              onChange={(e) => handleChange('wordsPerChunk', parseInt(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer accent-brand bg-slate-200"
            />
            <p className="text-[11px] text-slate-400">
              {t(themeSettings.wordsPerChunk <= 4 ? 'Short bursts' : themeSettings.wordsPerChunk <= 8 ? 'Balanced' : 'Long phrases')}
            </p>
          </div>
        )}

        {/* Characters per Chunk - shown when mode is 'characters' */}
        {themeSettings.chunkMode === 'characters' && (
          <div className="space-y-3 p-4 rounded-2xl border shadow-sm bg-white border-slate-200">
            <div className="flex justify-between text-sm font-bold text-slate-700">
              <span>{t('Characters per chunk')}</span>
              <span className="text-brand">{themeSettings.charsPerChunk}</span>
            </div>
            <input
              type="range"
              min="10"
              max="150"
              step="5"
              value={themeSettings.charsPerChunk || 50}
              onChange={(e) => handleChange('charsPerChunk', parseInt(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer accent-brand bg-slate-200"
            />
            <p className="text-[11px] text-slate-400">
              {t(themeSettings.charsPerChunk <= 30 ? 'Compact' : themeSettings.charsPerChunk <= 80 ? 'Balanced' : 'Extended')}
            </p>
          </div>
        )}
        
        {/* Separate Scripts Toggle - only show for Arabic */}
        {!isNonArabic && (
        <label className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm cursor-pointer transition-all ${
          themeSettings.separateScriptChunks 
            ? 'bg-brand/5 border-brand/30' 
            : 'bg-white border-slate-200 hover:border-slate-300'
        }`}>
          <div className="flex-1 min-w-0 me-4">
            <div className={`text-sm font-bold transition-colors ${themeSettings.separateScriptChunks ? 'text-brand' : 'text-slate-700'}`}>{t('Separate Latin/Arabic')}</div>
            <div className="text-xs mt-0.5 text-slate-500">{t('Keep Latin phrases in separate chunks')}</div>
          </div>
          <div 
            className={`relative shrink-0 rounded-full transition-colors ${themeSettings.separateScriptChunks ? 'bg-brand' : 'bg-slate-300'}`}
            style={{ width: 44, height: 24 }}
          >
            <input
              type="checkbox"
              checked={themeSettings.separateScriptChunks || false}
              onChange={(e) => handleChange('separateScriptChunks', e.target.checked)}
              className="sr-only"
            />
            <span 
              className="absolute bg-white rounded-full shadow transition-all duration-200"
              style={{ 
                width: 18, 
                height: 18, 
                top: 3,
                insetInlineStart: themeSettings.separateScriptChunks ? 23 : 3
              }}
            />
          </div>
        </label>
        )}

        {/* Uppercase Everything - only for non-Arabic languages */}
        {isNonArabic && (
        <label className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm cursor-pointer transition-all ${
          themeSettings.uppercaseEverything
            ? 'bg-brand/5 border-brand/30'
            : 'bg-white border-slate-200 hover:border-slate-300'
        }`}>
          <div className="flex-1 min-w-0 me-4">
            <div className={`text-sm font-bold transition-colors ${themeSettings.uppercaseEverything ? 'text-brand' : 'text-slate-700'}`}>{t('Uppercase everything')}</div>
            <div className="text-xs mt-0.5 text-slate-500">{t('Display and export all text in uppercase')}</div>
          </div>
          <div
            className={`relative shrink-0 rounded-full transition-colors ${themeSettings.uppercaseEverything ? 'bg-brand' : 'bg-slate-300'}`}
            style={{ width: 44, height: 24 }}
          >
            <input
              type="checkbox"
              checked={themeSettings.uppercaseEverything || false}
              onChange={(e) => handleChange('uppercaseEverything', e.target.checked)}
              className="sr-only"
            />
            <span
              className="absolute bg-white rounded-full shadow transition-all duration-200"
              style={{
                width: 18,
                height: 18,
                top: 3,
                insetInlineStart: themeSettings.uppercaseEverything ? 23 : 3
              }}
            />
          </div>
        </label>
        )}

        <label className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm cursor-pointer transition-all ${
          themeSettings.silenceAwareChunks 
            ? 'bg-brand/5 border-brand/30' 
            : 'bg-white border-slate-200 hover:border-slate-300'
        }`}>
          <div className="flex-1 min-w-0 me-4">
            <div className={`text-sm font-bold transition-colors ${themeSettings.silenceAwareChunks ? 'text-brand' : 'text-slate-700'}`}>{t('Silence-aware chunks')}</div>
            <div className="text-xs mt-0.5 text-slate-500">{t('Split chunks when silences appear')}</div>
          </div>
          <div 
            className={`relative shrink-0 rounded-full transition-colors ${themeSettings.silenceAwareChunks ? 'bg-brand' : 'bg-slate-300'}`}
            style={{ width: 44, height: 24 }}
          >
            <input
              type="checkbox"
              checked={themeSettings.silenceAwareChunks || false}
              onChange={(e) => handleChange('silenceAwareChunks', e.target.checked)}
              className="sr-only"
            />
            <span 
              className="absolute bg-white rounded-full shadow transition-all duration-200"
              style={{ 
                width: 18, 
                height: 18, 
                top: 3,
                insetInlineStart: themeSettings.silenceAwareChunks ? 23 : 3
              }}
            />
          </div>
        </label>

        <label className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm cursor-pointer transition-all ${
          themeSettings.punctuationAwareChunks 
            ? 'bg-brand/5 border-brand/30' 
            : 'bg-white border-slate-200 hover:border-slate-300'
        }`}>
          <div className="flex-1 min-w-0 me-4">
            <div className={`text-sm font-bold transition-colors ${themeSettings.punctuationAwareChunks ? 'text-brand' : 'text-slate-700'}`}>{t('Punctuation-aware chunks')}</div>
            <div className="text-xs mt-0.5 text-slate-500">{t('Split chunks after punctuation')}</div>
          </div>
          <div 
            className={`relative shrink-0 rounded-full transition-colors ${themeSettings.punctuationAwareChunks ? 'bg-brand' : 'bg-slate-300'}`}
            style={{ width: 44, height: 24 }}
          >
            <input
              type="checkbox"
              checked={themeSettings.punctuationAwareChunks || false}
              onChange={(e) => handleChange('punctuationAwareChunks', e.target.checked)}
              className="sr-only"
            />
            <span 
              className="absolute bg-white rounded-full shadow transition-all duration-200"
              style={{ 
                width: 18, 
                height: 18, 
                top: 3,
                insetInlineStart: themeSettings.punctuationAwareChunks ? 23 : 3
              }}
            />
          </div>
        </label>
        
      </div>

      {/* Advanced Speaker Settings — gated by REACT_APP_ENABLE_SPEAKER_FEATURES */}
      {process.env.REACT_APP_ENABLE_SPEAKER_FEATURES === 'true' && (
      <div className="space-y-4" data-tour="settings-advanced">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('Advanced · Speaker')}</h4>
        {!hasSpeakerInfo && (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-700">
            {t('No speaker data detected in the current captions. These settings will take effect once speaker information is available.')}
          </div>
        )}

        {/* Split by Speaker */}
        <label className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm cursor-pointer transition-all ${
          themeSettings.splitBySpeaker
            ? 'bg-brand/5 border-brand/30'
            : 'bg-white border-slate-200 hover:border-slate-300'
        }`}>
          <div className="flex-1 min-w-0 me-4">
            <div className={`text-sm font-bold transition-colors ${themeSettings.splitBySpeaker ? 'text-brand' : 'text-slate-700'}`}>{t('Split by Speaker')}</div>
            <div className="text-xs mt-0.5 text-slate-500">{t('Start a new chunk when the speaker changes')}</div>
          </div>
          <div
            className={`relative shrink-0 rounded-full transition-colors ${themeSettings.splitBySpeaker ? 'bg-brand' : 'bg-slate-300'}`}
            style={{ width: 44, height: 24 }}
          >
            <input
              type="checkbox"
              checked={themeSettings.splitBySpeaker || false}
              onChange={(e) => handleChange('splitBySpeaker', e.target.checked)}
              className="sr-only"
            />
            <span
              className="absolute bg-white rounded-full shadow transition-all duration-200"
              style={{
                width: 18,
                height: 18,
                top: 3,
                insetInlineStart: themeSettings.splitBySpeaker ? 23 : 3
              }}
            />
          </div>
        </label>

        {/* Speaker Color Toggle */}
        <label className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm cursor-pointer transition-all ${
          themeSettings.speakerColorEnabled
            ? 'bg-brand/5 border-brand/30'
            : 'bg-white border-slate-200 hover:border-slate-300'
        }`}>
          <div className="flex-1 min-w-0 me-4">
            <div className={`text-sm font-bold transition-colors ${themeSettings.speakerColorEnabled ? 'text-brand' : 'text-slate-700'}`}>{t('Color by Speaker')}</div>
            <div className="text-xs mt-0.5 text-slate-500">
              {rich(t('Alternate between <dark>dark</dark> and <rose>rose</rose> text per speaker'), {
                dark: (c) => <span className="font-bold" style={{color:'#1e293b'}}>{c}</span>,
                rose: (c) => <span className="font-bold" style={{color:'#9f1239'}}>{c}</span>,
              })}
            </div>
          </div>
          <div
            className={`relative shrink-0 rounded-full transition-colors ${themeSettings.speakerColorEnabled ? 'bg-brand' : 'bg-slate-300'}`}
            style={{ width: 44, height: 24 }}
          >
            <input
              type="checkbox"
              checked={themeSettings.speakerColorEnabled || false}
              onChange={(e) => handleChange('speakerColorEnabled', e.target.checked)}
              className="sr-only"
            />
            <span
              className="absolute bg-white rounded-full shadow transition-all duration-200"
              style={{
                width: 18,
                height: 18,
                top: 3,
                insetInlineStart: themeSettings.speakerColorEnabled ? 23 : 3
              }}
            />
          </div>
        </label>

        {/* Speaker Dash Toggle */}
        <label className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm cursor-pointer transition-all ${
          themeSettings.speakerDashEnabled
            ? 'bg-brand/5 border-brand/30'
            : 'bg-white border-slate-200 hover:border-slate-300'
        }`}>
          <div className="flex-1 min-w-0 me-4">
            <div className={`text-sm font-bold transition-colors ${themeSettings.speakerDashEnabled ? 'text-brand' : 'text-slate-700'}`}>{t('Dash on Speaker Change')}</div>
            <div className="text-xs mt-0.5 text-slate-500">{rich(t('Add a <dash>–</dash> before the first word of a new speaker'), { dash: (c) => <span className="font-mono font-bold">{c}</span> })}</div>
          </div>
          <div
            className={`relative shrink-0 rounded-full transition-colors ${themeSettings.speakerDashEnabled ? 'bg-brand' : 'bg-slate-300'}`}
            style={{ width: 44, height: 24 }}
          >
            <input
              type="checkbox"
              checked={themeSettings.speakerDashEnabled || false}
              onChange={(e) => handleChange('speakerDashEnabled', e.target.checked)}
              className="sr-only"
            />
            <span
              className="absolute bg-white rounded-full shadow transition-all duration-200"
              style={{
                width: 18,
                height: 18,
                top: 3,
                insetInlineStart: themeSettings.speakerDashEnabled ? 23 : 3
              }}
            />
          </div>
        </label>
      </div>
      )}

      {/* Stats */}
      <div className="pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>{t('{count} words', { count: captionData?.words?.length || 0 })}</span>
          <span>•</span>
          <span>{t('{count} chunks', { count: captionData?.skeleton?.length || 0 })}</span>
          <span>•</span>
          <span>{t('{count} phrases', { count: captionData?.phrases?.length || 0 })}</span>
          </div>
      </div>
    </div>
  );
};

export default Sidebar;
